import React from 'react';
import uniq from 'lodash/uniq';
import isNil from 'lodash/isNil';
import capitalize from 'lodash/capitalize';
import classNames from 'classnames';
import styled from '@emotion/styled';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { Checkbox } from '@atlaskit/checkbox';
import { SkeletonItem } from '@atlaskit/menu';
import { PricingPlanType } from '@atlassian/cssxp-typescommon';
import { Maybe, EndCustomerContact } from '@css-xp/graphtypes/src/generated-types-new';
import { FormattedDate } from '@css-xp/base';
import { ROVO_PRODUCT_ID } from '@css-xp/ppc-constants';
import { NoData, FormatCurrency } from '@css-xp/ppc-common';
import {
  PlanTypeCell,
  ActiveUsersCell,
  EntitlementItemCell,
  ProductStatusLozenge,
} from '@css-xp/ppc-purchase-experience';
import { CartProductSchema, OperationType } from '../../../../types';
import { Product, ProductIdPlanOptionsMap, cartLinkedEntitlement } from '../types';

import { ViewProductPromotions } from './view-product-promotions';
import { usePricingPlanEligibility, useStore, useDestinationBillingProfileDetails } from '../../../../store';
// eslint-disable-next-line workspaces/require-dependency
import { CustomerContactsAvatarGroup } from '@css-xp/ppc-subscription-table';
import { EnrichmentStatus, State } from '../../../../store/types';
import { getTrialStatusForEntitlement } from '../../../../store/utils/get-trial-status-for-entitlement';
import { splitAndCapitalize } from '@css-xp/ppc-utils';

type CartLineItemProps = {
  lineItem: cartLinkedEntitlement | Product;
  isApp: boolean;
  productIdPlanOptionsMap: ProductIdPlanOptionsMap;
  selectedBillDate?: string;
  setSelectedPricingPlanId: (
    updatedPricingPlanId: string,
    updatedOfferingId: string,
    productId: CartProductSchema['productId']
  ) => void;
  setActiveUsers: (plan: string, productId: CartProductSchema['productId']) => void;
  onRemoveProductFromCart: (productId: CartProductSchema['productId']) => void;
  handleCheckedProductLineItems: (productId: string) => void;
  isChecked: boolean;
  selectiveProductRemovalEnable: boolean;
  showTotalColumn?: boolean;
  showTaxEstimationColumn?: boolean;
  showProductsPromotionView?: boolean;
  shouldShowTrialStatus?: boolean;
  matchBillDateEnabled?: boolean;
  checkedBillDate?: Record<string, string>;
  enrichmentStatus: State['enrichmentStatus'];
  isAllowedToEditUsersCount?: boolean;
  isRowHighlighedForReview?: boolean;
  hideCustomerContactColumn?: boolean;
  isOrgProduct?: boolean;
  showSandboxProducts?: boolean;
  isSandboxDecouplingEnabled?: boolean;
  isNonCommercialPricingEnabled?: boolean;
};

const SpacingBeforeIntegratedApps = styled.div({
  padding: '12px',
});

const StyledLoaderWrapper = styled.span({
  '> div': {
    paddingLeft: 0,
  },
});

export const CartLineItemRow = ({
  lineItem,
  isApp = false,
  productIdPlanOptionsMap,
  selectedBillDate,
  setSelectedPricingPlanId,
  setActiveUsers,
  onRemoveProductFromCart,
  handleCheckedProductLineItems,
  isChecked,
  selectiveProductRemovalEnable,
  checkedBillDate,
  showProductsPromotionView = false,
  showTotalColumn = false,
  showTaxEstimationColumn = false,
  shouldShowTrialStatus = false,
  matchBillDateEnabled = false,
  enrichmentStatus = {},
  isAllowedToEditUsersCount,
  isRowHighlighedForReview,
  hideCustomerContactColumn = false,
  isOrgProduct = false,
  showSandboxProducts = false,
  isSandboxDecouplingEnabled = false,
  isNonCommercialPricingEnabled = false,
}: CartLineItemProps) => {
  const [{ products, selectedLinkedEntitlements, offeringsAndpricingPlans, selectedProducts }] = useStore();
  const productIdPriceEligibilityMap = usePricingPlanEligibility();
  const billingProfileDetails = useDestinationBillingProfileDetails();
  const selectedCurrency = billingProfileDetails?.currency;
  const appProductStatuses = isApp ? selectedLinkedEntitlements?.[lineItem.id]?.productStatuses ?? [] : []; // For apps use the updated status from selected app from store
  const productStatuses = uniq([...(lineItem.productStatuses ?? []), ...appProductStatuses]);

  const hasSandboxesLinked =
    showSandboxProducts &&
    Boolean(
      Object.values(products)?.find(
        (product) =>
          selectedProducts?.[product?.id] && product?.isSandbox && lineItem?.linkedSandboxIds?.includes(product?.id)
      )
    );

  const hasLinkedSandboxesToEntitlement =
    isSandboxDecouplingEnabled && lineItem?.linkedSandboxIds ? lineItem?.linkedSandboxIds?.length > 0 : false;

  const isInTrial = (() => {
    const currentOfferingId = products?.[lineItem.id]?.offeringId;
    const updatedOfferingId = lineItem.offeringId;

    // if initial offering id and updated offering id are same, then we can use the inTrial status from the product
    if (currentOfferingId === updatedOfferingId) return lineItem?.inTrial;

    // if the product is an app, then we can use the inTrial status from the app
    if (isApp) return lineItem?.inTrial || OperationType.NEW === lineItem.operationType;

    // we are calculating the trial status based on initial offering id and updated offering id
    return getTrialStatusForEntitlement({
      currentOfferingId: currentOfferingId,
      updatedOfferingId: updatedOfferingId,
      productId: products?.[lineItem.id]?.productId,
      inCurrentlyInTrial: !!products?.[lineItem.id]?.inTrial,
      offeringsAndpricingPlans: offeringsAndpricingPlans || {},
    });
  })();

  /**
   * Show the promotion list for product level
   * Show promotion list for apps level always as this feature was already implemented
   */
  const showPromotionList = showProductsPromotionView || isApp;
  const hasAllData = lineItem.hasAllData;
  const isLoadingEnrichment = enrichmentStatus[lineItem.id] === EnrichmentStatus.PENDING;
  const shouldShowInlineLoader = !hasAllData && isLoadingEnrichment;

  const renderInlineLoaderOrContent = ({ content }: { content: JSX.Element }) => {
    if (shouldShowInlineLoader) {
      return (
        <StyledLoaderWrapper data-testId="inline-loader">
          <SkeletonItem isShimmering />
        </StyledLoaderWrapper>
      );
    }

    return content;
  };

  const renderPlanForApp = (
    offeringName?: string,
    isNonCommercialPricingEnabled?: boolean,
    isApp?: boolean,
    priceEligibility?: `${PricingPlanType}`
  ) => {
    if (!offeringName) return <NoData />;
    if (!isNonCommercialPricingEnabled || !priceEligibility || !isApp) return offeringName;

    return (
      <div>
        <div>{offeringName}</div>
        <div className="product-rows-container--price-eligibility">{splitAndCapitalize(priceEligibility)}</div>
      </div>
    );
  };

  /**
   * isTransferStandardPaid is true if the product is a transfer and the offering is standard and the product is not in trial.
   * This is used to filter out the premium offering from the plan type dropdown in cart.
   *
   * Fetching products offering from store i.e products?.[lineItem.id].offeringId and not from lineItem.offeringId directly,
   * because say for isTransferStandardPaid lineitem we show offering standard and free, and we change plan type to free in cart not submitting the order,
   * in this case isTransferStandardPaid is false as offering for lineItem is changed to free.
   */
  const isTransferStandardPaid = !isApp
    ? lineItem.operationType === OperationType.TRANSFER &&
      productIdPlanOptionsMap[lineItem.id]?.find((option) => option.offeringId === products?.[lineItem.id].offeringId)
        ?.offeringName === 'Standard' &&
      lineItem.inTrial === false
    : false;

  const isEnabledToEditUsers = (isAllowedToEditUsersCount || lineItem.operationType === OperationType.NEW) && !isApp;

  return (
    <tr
      key={lineItem.id}
      className={classNames('product-rows-container--row', {
        'needs-review-row': isRowHighlighedForReview,
      })}
    >
      <td>
        <div className="product-rows-container--product-wrapper">
          {isApp && lineItem.operationType !== OperationType.NEW ? (
            selectiveProductRemovalEnable ? (
              <SpacingBeforeIntegratedApps />
            ) : null
          ) : selectiveProductRemovalEnable ? (
            <Checkbox
              testId={`select-product-checkbox-${lineItem?.id}`}
              isChecked={isChecked}
              onChange={() => handleCheckedProductLineItems(lineItem.id)}
              label=""
            />
          ) : (
            <Button
              iconBefore={<CrossIcon size="small" label="" />}
              appearance="subtle"
              className="product-rows-container--delete-btn"
              onClick={() => onRemoveProductFromCart(lineItem.id)}
              testId="remove-product-from-cart-btn"
            />
          )}
          <EntitlementItemCell
            name={lineItem?.name}
            logoId={lineItem?.logoId}
            productId={lineItem?.productId}
            entitlementNumber={lineItem?.entitlementNumber}
            inTrial={shouldShowTrialStatus && isInTrial}
            shouldShowSandboxStatus={
              (showSandboxProducts || isSandboxDecouplingEnabled) &&
              lineItem.isSandbox &&
              lineItem.productId !== ROVO_PRODUCT_ID
            }
            inUpgradeTrial={lineItem.isOnUpgradeTrial}
          />
        </div>
      </td>
      <td>
        {isApp || (isOrgProduct && lineItem.productId === ROVO_PRODUCT_ID) || lineItem.isSandbox ? (
          renderPlanForApp(
            lineItem?.offeringName,
            isNonCommercialPricingEnabled,
            isApp,
            productIdPriceEligibilityMap[lineItem.productId]
          ) ?? <NoData />
        ) : (
          <PlanTypeCell
            offeringId={lineItem.offeringId}
            offeringName={lineItem?.offeringName}
            offeringLevel={lineItem?.offeringLevel}
            setSelectedPricingPlanId={(updatedPricingPlanId, updatedOfferingId) => {
              setSelectedPricingPlanId(updatedPricingPlanId, updatedOfferingId, lineItem.id);
            }}
            offeringPricingPlanOptions={productIdPlanOptionsMap[lineItem.id]}
            isTransferStandardPaid={isTransferStandardPaid}
            hasSandboxesLinked={hasSandboxesLinked || hasLinkedSandboxesToEntitlement}
            selectedOrDefaultPriceEligibility={
              isNonCommercialPricingEnabled ? productIdPriceEligibilityMap[lineItem.productId] : undefined
            }
            isOnUpgradeTrial={lineItem.isOnUpgradeTrial}
          />
        )}
      </td>
      {!hideCustomerContactColumn && (
        <td>
          <div
            className="product-rows-container--ecc-avatar-column"
            data-testId="subscription-end-customer-contact-column"
          >
            {renderInlineLoaderOrContent({
              content: (
                <CustomerContactsAvatarGroup
                  contacts={lineItem?.customerContacts as Maybe<Array<EndCustomerContact>> | undefined}
                />
              ),
            })}
          </div>
        </td>
      )}
      {showTotalColumn && (
        <td>
          <div>
            {!isNil(lineItem.total) ? (
              <FormatCurrency value={lineItem.total} showCurrencyCode currency={selectedCurrency} showCommas />
            ) : (
              <NoData />
            )}
          </div>
        </td>
      )}
      <td>
        <div>
          {!isNil(lineItem.listPrice) ? (
            <FormatCurrency value={lineItem.listPrice} showCurrencyCode currency={selectedCurrency} showCommas />
          ) : (
            <NoData />
          )}
        </div>
      </td>
      <td>
        <div>
          {!isNil(lineItem.partnerMargin) ? (
            <FormatCurrency value={lineItem.partnerMargin} showCurrencyCode currency={selectedCurrency} showCommas />
          ) : (
            <NoData />
          )}
        </div>
      </td>
      <td>
        {renderInlineLoaderOrContent({
          content: (
            <div className="product-rows-container--app-name-column" data-testid="app-other-discount">
              {!isNil(lineItem.otherDiscount) ? (
                <div>
                  <FormatCurrency
                    value={lineItem.otherDiscount}
                    showCurrencyCode
                    currency={selectedCurrency}
                    showCommas
                  />
                </div>
              ) : (
                <NoData />
              )}
              {showPromotionList &&
                renderInlineLoaderOrContent({
                  content: <ViewProductPromotions isApp={isApp} productId={lineItem?.productId} id={lineItem?.id} />,
                })}
            </div>
          ),
        })}
      </td>
      {showTaxEstimationColumn && (
        <td>
          {!isNil(lineItem.totalTax) ? (
            <div>
              <FormatCurrency value={lineItem.totalTax} showCurrencyCode currency={selectedCurrency} showCommas />
            </div>
          ) : (
            <NoData />
          )}
        </td>
      )}
      <td>
        {renderInlineLoaderOrContent({
          content: (
            <div className="product-rows-container--next-bill-date-column">
              {matchBillDateEnabled ? (
                checkedBillDate && checkedBillDate[lineItem.id] ? (
                  <>
                    <FormattedDate date={checkedBillDate[lineItem.id]} />
                    <Tooltip content="Next bill date has been edited">
                      <InfoIcon size="small" label="" />
                    </Tooltip>
                  </>
                ) : (
                  <FormattedDate date={lineItem.nextBillDate} />
                )
              ) : selectedBillDate ? (
                <>
                  <FormattedDate date={selectedBillDate} />
                  <Tooltip content="Next bill date has been edited">
                    <InfoIcon size="small" label="" />
                  </Tooltip>
                </>
              ) : (
                <FormattedDate date={lineItem.nextBillDate} />
              )}
            </div>
          ),
        })}
      </td>
      <td>
        {renderInlineLoaderOrContent({
          content: (
            <>
              {isEnabledToEditUsers ? (
                <ActiveUsersCell
                  activeUsers={lineItem.activeUsers}
                  setActiveUsers={(updatedActiveUsers) => {
                    setActiveUsers(updatedActiveUsers, lineItem.id);
                  }}
                />
              ) : (
                lineItem.activeUsers
              )}
            </>
          ),
        })}
      </td>
      <td>
        {renderInlineLoaderOrContent({
          content: <div>{capitalize(lineItem.billingCycle)}</div>,
        })}
      </td>
      <td>
        {productStatuses?.map((status) => {
          return (
            <ProductStatusLozenge className="product-status-column" key={`product-status-${status}`} status={status} />
          );
        })}
      </td>
    </tr>
  );
};
